import { Box, Button, CircularProgress, Grid, Paper, Stack, Typography } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { observer } from 'mobx-react-lite'
import { FC, ReactNode, useCallback, useEffect, useState } from 'react'
import { EditIcon } from '../../icons'
import { IconButton } from '../button/IconButton'

export type DataControlCardProps = {
  title: string | ReactNode
  dataCy?: string
  viewComponent: ReactNode
  editComponent?: ReactNode
  autoFocusId?: string
  // [Review] TODO: the naming of the two properties below is a bit confusing, maybe rename them?
  allowEdit?: boolean
  hideActions?: boolean
  viewDisplay?: 'flex' | 'block' | 'grid' | 'inline'
  saveLabel?: string
  defaultEditMode?: boolean
  onClickEdit?: () => void
  onClickSave?: () => Promise<void>
  onClickCancel?: () => void
}

type EditActionProps = { setIsEditMode: (isEditMode: boolean) => void } & Pick<
  DataControlCardProps,
  'autoFocusId' | 'allowEdit' | 'onClickEdit' | 'dataCy'
>

const EditAction: FC<EditActionProps> = props => {
  const { autoFocusId, allowEdit, setIsEditMode, onClickEdit, dataCy } = props

  return (
    <IconButton
      data-cy={dataCy ? `${dataCy}-edit-btn` : ''}
      variant='contained'
      color='default'
      onClick={() => {
        if (onClickEdit) {
          onClickEdit()
        }

        if (autoFocusId) {
          setTimeout(() => {
            document.getElementById(autoFocusId)?.focus()
          }, 100)
        }

        if (allowEdit) {
          setIsEditMode(true)
        }
      }}
    >
      <EditIcon />
    </IconButton>
  )
}

type formActionsType = {
  setIsEditMode: (isEditMode: boolean) => void
  saveLabel?: string
} & Pick<DataControlCardProps, 'onClickSave' | 'onClickCancel'>

const FormActions: FC<formActionsType> = props => {
  const { setIsEditMode, onClickSave, onClickCancel, saveLabel } = props
  const { t } = useTranslate()

  const [isSaving, setIsSaving] = useState(false)

  const handleSave = useCallback(async () => {
    setIsSaving(true)
    try {
      if (onClickSave) {
        await onClickSave()
      }
    } catch (error) {
      console.error('Error saving:', error)
    } finally {
      setIsSaving(false)
    }
  }, [onClickSave, setIsEditMode])

  return !onClickSave && !onClickCancel ? (
    <></>
  ) : (
    <Stack direction='row' spacing={1}>
      {onClickCancel && (
        <Button
          variant='text'
          color='inherit'
          onClick={() => {
            onClickCancel?.()
            setIsEditMode(false)
          }}
        >
          {t('cancel', 'Cancel')}
        </Button>
      )}

      {onClickSave && (
        <Button
          disabled={isSaving}
          variant='contained'
          color='primary'
          onClick={() => {
            handleSave()
          }}
          data-cy='vessel-visit-header-submit-btn'
          type='button'
          startIcon={isSaving && <CircularProgress size='1rem' />}
        >
          {saveLabel ?? t('save', 'Save')}
        </Button>
      )}
    </Stack>
  )
}

export const DataControlCard = observer((props: DataControlCardProps) => {
  const {
    title,
    viewComponent,
    editComponent,
    hideActions,
    viewDisplay,
    saveLabel,
    defaultEditMode: isCardActive = false,
    dataCy,
  } = props

  const [isEditMode, setIsEditMode] = useState(isCardActive)

  useEffect(() => {
    if (isCardActive) {
      setIsEditMode(true)
    }
  }, [isCardActive])

  return (
    <Paper variant='outlined' sx={{ p: '1rem' }}>
      <Grid container direction={'column'}>
        <Grid paddingBottom={1} size={12}>
          <Stack direction='row' justifyContent='space-between' alignItems='center'>
            <Typography variant='h4'>{title}</Typography>
            {!hideActions &&
              (isEditMode ? (
                <FormActions {...props} setIsEditMode={setIsEditMode} saveLabel={saveLabel} />
              ) : (
                <EditAction {...props} setIsEditMode={setIsEditMode} dataCy={dataCy} />
              ))}
          </Stack>
        </Grid>
        <Grid size={{ sm: 12 }}>
          <Box sx={{ display: isEditMode ? 'flex' : 'none' }}>{editComponent}</Box>
          <Box sx={{ display: !isEditMode ? viewDisplay : 'none' }}>{viewComponent}</Box>
        </Grid>
      </Grid>
    </Paper>
  )
})
